import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { AuthProvider, hasAuthParams } from 'react-oidc-context';
import type { User } from 'oidc-client-ts';

import { LoadingState } from '@/components/common/LoadingState';
import { AccountWatch } from '@/lib/auth/accountWatch';
import { isFirstEntry, SECTION_PATH, seedParent } from '@/lib/bookNavigation';
import { LibraryAccessWatch } from '@/lib/auth/LibraryAccessWatch';
import { SessionRecovery } from '@/lib/auth/SessionRecovery';
import { getUserManager, type SingleFlightUserManager } from '@/lib/auth/userManager';

export interface OidcAppProviderProps {
  children: ReactNode;
  userManager?: SingleFlightUserManager;
}

export function resolveReturnTo(state: unknown): string {
  const target = (state as { returnTo?: unknown } | undefined)?.returnTo;
  return typeof target === 'string' && target.startsWith('/') ? target : '/';
}

let returned: string | null = null;

/**
 * Leaves the sign-in callback for the address the reader signed in from, once
 * (the provider's callback and the callback page both ask). The sign-in page
 * was the tab's first entry: a section returned to gets its document behind
 * it (§3.3).
 */
export function returnAfterSignIn(navigate: (to: string, options?: { replace?: boolean; state?: unknown }) => unknown, target: string): void {
  if (returned === target && window.location.pathname !== '/auth/callback') return;
  returned = target;
  const section = SECTION_PATH.exec(target);
  if (section && isFirstEntry()) seedParent((path, options) => void navigate(path, options), `/d/${section[1]}`, decodeURIComponent(section[2]!), target);
  else void navigate(target, { replace: true });
}

/**
 * Mounts react-oidc-context only after the stored session has had its one
 * chance to refresh, so the provider's first read of the user already sees
 * the restored grant and the app never decides "signed out" on a stale token.
 * A sign-in callback carries its own fresh grant and skips the attempt.
 */
export function OidcAppProvider({ children, userManager }: OidcAppProviderProps) {
  const navigate = useNavigate();
  const [manager] = useState(() => userManager ?? getUserManager());
  const [account] = useState(() => new AccountWatch(manager));
  const [restored, setRestored] = useState(() => hasAuthParams());

  useEffect(() => {
    if (restored) return;
    let active = true;
    void manager
      .restoreSession()
      .catch(() => null)
      .then((user) => {
        if (!active) return;
        account.adopt(user?.profile.sub);
        setRestored(true);
      });
    return () => {
      active = false;
    };
  }, [manager, account, restored]);

  useEffect(() => (restored ? account.watch() : undefined), [account, restored]);

  const onSigninCallback = (user: User | undefined) => {
    returnAfterSignIn(navigate, resolveReturnTo(user?.state));
  };

  if (!restored) return <LoadingState label="signing in…" />;

  return (
    <AuthProvider userManager={manager} onSigninCallback={onSigninCallback}>
      <SessionRecovery />
      <LibraryAccessWatch />
      {children}
    </AuthProvider>
  );
}
